import React, { useState, useEffect } from 'react';
import { useDashboardTranslation } from '../../i18n';
import { Activity, CheckCircle, Info, GripVertical, Plus, X as XIcon } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { WidgetPresentationType, MappingConfig, AggregationType } from '../../types';

interface Step2FieldMappingProps {
  presentationType: WidgetPresentationType;
  jsonPreview: Record<string, unknown>[] | null;
  mappingConfig: MappingConfig | null;
  onMappingConfigChange: (config: MappingConfig) => void;
  /** Si true (modo catálogo), muestra banner de auto-fill */
  isCatalogMode?: boolean;
  /** Path pre-rellenado del campo valor (desde el catálogo) */
  campoValorPrefill?: string;
  /** Path pre-rellenado del campo fecha (desde el catálogo) */
  campoFechaPrefill?: string;
  /** Nombre del indicador seleccionado (para el banner) */
  indicatorName?: string;
}

/**
 * Clasifica los campos del primer registro del JSON preview.
 * Aplana objetos anidados a dot-notation (ej: name.common).
 */
function classifyFields(sample: Record<string, unknown>, prefix = '') {
  const textFields: string[] = [];
  const numericFields: string[] = [];
  const dateFields: string[] = [];
  const allFields: string[] = [];

  for (const [key, val] of Object.entries(sample)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (typeof val === 'number') {
      allFields.push(fullKey);
      numericFields.push(fullKey);
    } else if (typeof val === 'string') {
      allFields.push(fullKey);
      if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
        dateFields.push(fullKey);
      } else {
        textFields.push(fullKey);
      }
    } else if (typeof val === 'boolean') {
      allFields.push(fullKey);
      textFields.push(fullKey);
    } else if (val != null && typeof val === 'object' && !Array.isArray(val)) {
      // Aplanar objetos anidados (ej: name: {common: "Colombia"} → name.common)
      const nested = classifyFields(val as Record<string, unknown>, fullKey);
      textFields.push(...nested.textFields);
      numericFields.push(...nested.numericFields);
      dateFields.push(...nested.dateFields);
      allFields.push(...nested.allFields);
    } else {
      // Arrays u otros tipos — solo agregar a allFields
      allFields.push(fullKey);
    }
  }

  return { textFields, numericFields, dateFields, allFields };
}

const AGGREGATIONS = [
  { value: 'sum', label: 'Suma' },
  { value: 'average', label: 'Promedio' },
  { value: 'count', label: 'Conteo' },
  { value: 'last', label: 'Último valor' },
  { value: 'min', label: 'Mínimo' },
  { value: 'max', label: 'Máximo' },
] as const;

const CHART_AGGREGATIONS = [
  { value: 'none', label: 'Ninguna (datos ya agrupados)' },
  { value: 'sum', label: 'Suma' },
  { value: 'average', label: 'Promedio' },
  { value: 'count', label: 'Conteo' },
  { value: 'min', label: 'Mínimo' },
  { value: 'max', label: 'Máximo' },
  { value: 'last', label: 'Último valor' },
  { value: 'first', label: 'Primer valor' },
] as const;

const selectClass = "w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";
const optionalBadge = <span className="text-gray-400 font-normal ml-1">(opcional)</span>;

export const Step2FieldMapping: React.FC<Step2FieldMappingProps> = ({
  presentationType,
  jsonPreview,
  mappingConfig,
  onMappingConfigChange,
  isCatalogMode = false,
  campoValorPrefill,
  campoFechaPrefill,
  indicatorName,
}) => {
  const { t } = useDashboardTranslation();
  if (!jsonPreview || jsonPreview.length === 0) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.builder.step2.title')}</h3>
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <Activity className="w-10 h-10 text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">{t('dashboard.builder.step2.noData')}</p>
        </div>
      </div>
    );
  }

  const { textFields, numericFields, dateFields, allFields } = classifyFields(jsonPreview[0]);
  const labelFields = [...textFields, ...dateFields];

  const renderKpiMapping = () => {
    const cfg = mappingConfig?.type === 'kpi' ? mappingConfig : null;
    return (
      <div className="space-y-4">
        <div>
          <label className={labelClass}>{t('dashboard.builder.step2.valueFieldLabel')} <span className="text-gray-400 font-normal">(numérico)</span></label>
          <select className={selectClass} value={cfg?.valueField ?? ''}
            onChange={e => onMappingConfigChange({ type: 'kpi', valueField: e.target.value, labelField: cfg?.labelField, aggregation: cfg?.aggregation ?? 'last' })}>
            <option value="">— Seleccionar —</option>
            {numericFields.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>Campo de etiqueta {optionalBadge}</label>
          <select className={selectClass} value={cfg?.labelField ?? ''}
            onChange={e => onMappingConfigChange({ ...cfg!, labelField: e.target.value || undefined })}>
            <option value="">— Ninguno —</option>
            {labelFields.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className={labelClass}>{t('dashboard.builder.step2.aggregation')}</label>
          <select className={selectClass} value={cfg?.aggregation ?? 'last'}
            onChange={e => onMappingConfigChange({ ...cfg!, aggregation: e.target.value as MappingConfig & { type: 'kpi' } extends { aggregation: infer A } ? A : never })}>
            {AGGREGATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Prefijo del valor {optionalBadge}</label>
            <input type="text" placeholder="ej: $, €, COP" className={selectClass}
              value={cfg?.valuePrefix ?? ''}
              onChange={e => onMappingConfigChange({ ...cfg!, valuePrefix: e.target.value || undefined })} />
          </div>
          <div>
            <label className={labelClass}>Sufijo del valor {optionalBadge}</label>
            <input type="text" placeholder="ej: hab., km², %" className={selectClass}
              value={cfg?.valueSuffix ?? ''}
              onChange={e => onMappingConfigChange({ ...cfg!, valueSuffix: e.target.value || undefined })} />
          </div>
        </div>
        {cfg?.valueField && (
          <SummaryBadge text={`${cfg.valuePrefix ?? ''}${cfg.valueField}${cfg.valueSuffix ? ' ' + cfg.valueSuffix : ''} — agregación: "${AGGREGATIONS.find(a => a.value === cfg.aggregation)?.label ?? cfg.aggregation}".`} />
        )}
      </div>
    );
  };

  const renderBarMapping = () => {
    const cfg = mappingConfig?.type === 'chart_bar_vertical' ? mappingConfig : null;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.axisXLabels')}</label>
            <select className={selectClass} value={cfg?.xField ?? ''}
              onChange={e => onMappingConfigChange({ type: 'chart_bar_vertical', xField: e.target.value, yField: cfg?.yField ?? '', groupByField: cfg?.groupByField })}>
              <option value="">— Seleccionar —</option>
              {labelFields.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.axisYValues')}</label>
            <select className={selectClass} value={cfg?.yField ?? ''}
              onChange={e => onMappingConfigChange({ type: 'chart_bar_vertical', xField: cfg?.xField ?? '', yField: e.target.value, groupByField: cfg?.groupByField })}>
              <option value="">— Seleccionar —</option>
              {numericFields.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Agrupar por {optionalBadge}</label>
          <select className={selectClass} value={cfg?.groupByField ?? ''}
            onChange={e => onMappingConfigChange({ ...cfg!, groupByField: e.target.value || undefined })}>
            <option value="">— Sin agrupación —</option>
            {labelFields.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        {/* Agregación — oculta si hay groupByField (stacked bars necesitan datos pre-agrupados) */}
        {!cfg?.groupByField && (
          <div>
            <label className={labelClass}>Cálculo (Agregación) {optionalBadge}</label>
            <select className={selectClass} value={cfg?.aggregation ?? 'none'}
              onChange={e => onMappingConfigChange({ ...cfg!, aggregation: e.target.value as AggregationType })}>
              {CHART_AGGREGATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <p className="text-[11px] text-gray-500 mt-0.5">Si el endpoint devuelve datos desagrupados, selecciona cómo calcular el valor por categoría.</p>
          </div>
        )}
        {cfg?.xField && cfg?.yField && (
          <SummaryBadge text={`Eje X: "${cfg.xField}", Eje Y: "${cfg.yField}".${cfg.groupByField ? ` Agrupado por "${cfg.groupByField}".` : ''}${cfg.aggregation && cfg.aggregation !== 'none' ? ` Agregación: ${CHART_AGGREGATIONS.find(a => a.value === cfg.aggregation)?.label}.` : ''}`} />
        )}
      </div>
    );
  };

  const renderLineMapping = () => {
    const cfg = mappingConfig?.type === 'chart_line' ? mappingConfig : null;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.axisXTime')}</label>
            <select className={selectClass} value={cfg?.xField ?? ''}
              onChange={e => onMappingConfigChange({ type: 'chart_line', xField: e.target.value, yField: cfg?.yField ?? '', seriesFields: cfg?.seriesFields })}>
              <option value="">— Seleccionar —</option>
              {[...labelFields, ...dateFields.filter(d => !labelFields.includes(d))].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.axisYValues')}</label>
            <select className={selectClass} value={cfg?.yField ?? ''}
              onChange={e => onMappingConfigChange({ type: 'chart_line', xField: cfg?.xField ?? '', yField: e.target.value, seriesFields: cfg?.seriesFields })}>
              <option value="">— Seleccionar —</option>
              {numericFields.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Series múltiples {optionalBadge}</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {numericFields.filter(f => f !== cfg?.yField).map(f => {
              const selected = cfg?.seriesFields?.includes(f) ?? false;
              return (
                <button key={f} type="button"
                  onClick={() => {
                    const current = cfg?.seriesFields ?? [];
                    const next = selected ? current.filter(s => s !== f) : [...current, f];
                    onMappingConfigChange({ ...cfg!, seriesFields: next.length > 0 ? next : undefined });
                  }}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${selected ? 'bg-blue-100 border-blue-400 text-blue-700' : 'bg-white border-gray-300 text-gray-600 hover:border-blue-300'}`}>
                  {f}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className={labelClass}>Cálculo (Agregación) {optionalBadge}</label>
          <select className={selectClass} value={cfg?.aggregation ?? 'none'}
            onChange={e => onMappingConfigChange({ ...cfg!, aggregation: e.target.value as AggregationType })}>
            {CHART_AGGREGATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <p className="text-[11px] text-gray-500 mt-0.5">Si el endpoint devuelve datos desagrupados, selecciona cómo calcular el valor por categoría.</p>
        </div>
        {cfg?.xField && cfg?.yField && (
          <SummaryBadge text={`Eje X: "${cfg.xField}", Eje Y: "${cfg.yField}"${cfg.seriesFields?.length ? `. Series: ${cfg.seriesFields.join(', ')}.` : '.'}${cfg.aggregation && cfg.aggregation !== 'none' ? ` Agregación: ${CHART_AGGREGATIONS.find(a => a.value === cfg.aggregation)?.label}.` : ''}`} />
        )}
      </div>
    );
  };

  const renderDonutMapping = () => {
    const cfg = mappingConfig?.type === 'chart_donut' ? mappingConfig : null;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.categoryField')}</label>
            <select className={selectClass} value={cfg?.categoryField ?? ''}
              onChange={e => onMappingConfigChange({ type: 'chart_donut', categoryField: e.target.value, valueField: cfg?.valueField ?? '' })}>
              <option value="">— Seleccionar —</option>
              {labelFields.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.valuesField')}</label>
            <select className={selectClass} value={cfg?.valueField ?? ''}
              onChange={e => onMappingConfigChange({ type: 'chart_donut', categoryField: cfg?.categoryField ?? '', valueField: e.target.value })}>
              <option value="">— Seleccionar —</option>
              {numericFields.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelClass}>Cálculo (Agregación) {optionalBadge}</label>
          <select className={selectClass} value={cfg?.aggregation ?? 'none'}
            onChange={e => onMappingConfigChange({ ...cfg!, aggregation: e.target.value as AggregationType })}>
            {CHART_AGGREGATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <p className="text-[11px] text-gray-500 mt-0.5">Si el endpoint devuelve datos desagrupados, selecciona cómo calcular el valor por categoría.</p>
        </div>
        {cfg?.categoryField && cfg?.valueField && (
          <SummaryBadge text={`Porciones por "${cfg.categoryField}", tamaño: "${cfg.valueField}".${cfg.aggregation && cfg.aggregation !== 'none' ? ` Agregación: ${CHART_AGGREGATIONS.find(a => a.value === cfg.aggregation)?.label}.` : ''}`} />
        )}
      </div>
    );
  };

  const renderTableMapping = () => {
    const cfg = mappingConfig?.type === 'table' ? mappingConfig : null;
    const selected = cfg?.visibleColumns ?? [];
    const available = allFields.filter(f => !selected.includes(f));

    const sensors = useSensors(
      useSensor(PointerSensor, { activationConstraint: { delay: 100, tolerance: 5 } }),
      useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = selected.indexOf(active.id as string);
      const newIdx = selected.indexOf(over.id as string);
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(selected, oldIdx, newIdx);
      onMappingConfigChange({ type: 'table', visibleColumns: reordered, sortField: cfg?.sortField, sortDirection: cfg?.sortDirection });
    };

    const addColumn = (field: string) => {
      onMappingConfigChange({ type: 'table', visibleColumns: [...selected, field], sortField: cfg?.sortField, sortDirection: cfg?.sortDirection });
    };

    const removeColumn = (field: string) => {
      onMappingConfigChange({ type: 'table', visibleColumns: selected.filter(c => c !== field), sortField: cfg?.sortField, sortDirection: cfg?.sortDirection });
    };

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          {/* Lista: Disponibles */}
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.availableColumns')}</label>
            <div className="border border-gray-200 rounded-lg bg-gray-50 min-h-[120px] max-h-[200px] overflow-y-auto p-1.5 space-y-1">
              {available.length === 0 ? (
                <p className="text-xs text-gray-400 p-2 text-center">{t('dashboard.builder.step2.allAssigned')}</p>
              ) : (
                available.map(f => (
                  <button key={f} onClick={() => addColumn(f)} type="button"
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-700 bg-white border border-gray-200 rounded hover:border-blue-300 hover:bg-blue-50 transition-colors text-left">
                    <Plus className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{f}</span>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Lista: Seleccionadas (sortable) */}
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.selectedColumns')}</label>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={selected} strategy={verticalListSortingStrategy}>
                <div className="border border-blue-200 rounded-lg bg-blue-50/30 min-h-[120px] max-h-[200px] overflow-y-auto p-1.5 space-y-1">
                  {selected.length === 0 ? (
                    <p className="text-xs text-gray-400 p-2 text-center">{t('dashboard.builder.step2.dragHint')}</p>
                  ) : (
                    selected.map((field, idx) => (
                      <SortableColumnItem key={field} id={field} index={idx} onRemove={removeColumn} />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>

        {/* Sort field + direction */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Ordenar por {optionalBadge}</label>
            <select className={selectClass} value={cfg?.sortField ?? ''}
              onChange={e => onMappingConfigChange({ ...cfg!, sortField: e.target.value || undefined })}>
              <option value="">— Sin ordenamiento —</option>
              {allFields.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>{t('dashboard.builder.step2.direction')}</label>
            <select className={selectClass} value={cfg?.sortDirection ?? 'asc'}
              onChange={e => onMappingConfigChange({ ...cfg!, sortDirection: e.target.value as 'asc' | 'desc' })}>
              <option value="asc">{t('dashboard.builder.step2.asc')}</option>
              <option value="desc">{t('dashboard.builder.step2.desc')}</option>
            </select>
          </div>
        </div>

        {selected.length > 0 && (
          <SummaryBadge text={`Se mostrarán ${selected.length} de ${allFields.length} columnas: ${selected.join(' → ')}${cfg?.sortField ? `. Orden: ${cfg.sortField} ${cfg.sortDirection === 'desc' ? '↓' : '↑'}` : ''}`} />
        )}
      </div>
    );
  };

  const renderMapping = () => {
    switch (presentationType) {
      case 'kpi': return renderKpiMapping();
      case 'chart_bar_vertical': return renderBarMapping();
      case 'chart_bar_horizontal': return renderBarMapping();
      case 'chart_line': return renderLineMapping();
      case 'chart_donut': return renderDonutMapping();
      case 'table': return renderTableMapping();
      default: return renderBarMapping();
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-800">{t('dashboard.builder.step2.title')}</h3>
        <p className="text-sm text-gray-600 mt-1">{t('dashboard.builder.step2.subtitle')}</p>
      </div>

      {/* Banner auto-fill desde catálogo */}
      {isCatalogMode && (campoValorPrefill || campoFechaPrefill) && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-green-800">
            <span className="font-semibold">{t('dashboard.builder.step2.autoMapping')}</span>
            {indicatorName && <span> — {indicatorName}</span>}
            <div className="mt-1 text-green-700 space-y-0.5">
              {campoValorPrefill && <div>{t('dashboard.builder.step2.detectedValue')} <code className="bg-green-100 px-1 rounded">{campoValorPrefill}</code></div>}
              {campoFechaPrefill && <div>{t('dashboard.builder.step2.detectedDate')} <code className="bg-green-100 px-1 rounded">{campoFechaPrefill}</code></div>}
            </div>
          </div>
        </div>
      )}

      {/* Campos disponibles del endpoint */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Campos detectados del endpoint
          <span className="ml-2 font-normal normal-case text-gray-400">({allFields.length} campo{allFields.length !== 1 ? 's' : ''})</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {textFields.map(f => (
            <span key={f} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border bg-blue-50 border-blue-200 text-blue-700">
              <span className="font-bold text-[9px] bg-blue-200 text-blue-800 px-1 rounded">A</span>
              {f}
            </span>
          ))}
          {dateFields.map(f => (
            <span key={f} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border bg-purple-50 border-purple-200 text-purple-700">
              <span className="font-bold text-[9px] bg-purple-200 text-purple-800 px-1 rounded">📅</span>
              {f}
            </span>
          ))}
          {numericFields.map(f => (
            <span key={f} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-700">
              <span className="font-bold text-[9px] bg-emerald-200 text-emerald-800 px-1 rounded">#</span>
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Mapping form per type */}
      {renderMapping()}
    </div>
  );
};

/** Item sortable dentro de la lista "Columnas seleccionadas" */
const SortableColumnItem: React.FC<{ id: string; index: number; onRemove: (id: string) => void }> = ({ id, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-1.5 px-2 py-1.5 text-xs bg-white border rounded transition-colors ${isDragging ? 'border-blue-400 shadow-md z-10' : 'border-gray-200'}`}>
      <span className="text-[10px] text-gray-400 font-mono w-4 text-right">{index + 1}</span>
      <button {...attributes} {...listeners} className="cursor-grab text-gray-400 hover:text-gray-600 touch-none" tabIndex={-1}>
        <GripVertical className="w-3 h-3" />
      </button>
      <span className="flex-1 truncate text-gray-700">{id}</span>
      <button onClick={() => onRemove(id)} className="text-gray-400 hover:text-red-600 transition-colors" type="button">
        <XIcon className="w-3 h-3" />
      </button>
    </div>
  );
};

/** Badge informativo azul con resumen */
const SummaryBadge: React.FC<{ text: string }> = ({ text }) => (
  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-start gap-2">
    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
    <span>{text}</span>
  </div>
);
